// IEventBus — 簡易型別安全 pub/sub
// 對應 PRD §3.2.8 IEventBus、§3.1 解耦原則
//
// 用途：
// - Game Core 透過 IEventBus 對外發出狀態變化事件（PRD §3.3 事件清單）
// - UI / Audio / AI 透過 IEventBus 訂閱事件被動反應
// - Core 與 UI 雙向解耦，兩邊只依賴事件型別契約
//
// 設計原則：
// - 用 generic TMap 鎖事件名稱與 payload 型別對應
// - on() 回傳 unsubscribe 函式，呼叫端不必記住 listener 即可取消訂閱
// - 同步派發（emit 後立即執行所有 listener）；不需要 async 排程

/** TListener 函式簽章。 */
export type TListener<TPayload> = (payload: TPayload) => void;

/**
 * IEventBus 公開介面。
 *
 * @typeParam TMap 事件名稱 → payload 型別的對應 record
 *   範例：{ 'tile.drawn': { seat: TSeat; tile: TTile } }
 */
export interface IEventBus<TMap extends Record<string, unknown>> {
  /** 派發事件給所有訂閱者。 */
  emit<K extends keyof TMap>(event: K, payload: TMap[K]): void;

  /** 訂閱事件。回傳 unsubscribe 函式，呼叫即取消訂閱。 */
  on<K extends keyof TMap>(event: K, listener: TListener<TMap[K]>): () => void;

  /** 取消訂閱（明確指定 listener）。 */
  off<K extends keyof TMap>(event: K, listener: TListener<TMap[K]>): void;

  /** 清空所有訂閱（測試 / Scene 切換時使用）。 */
  clear(): void;
}

/**
 * 建立一個全新的 IEventBus 實例。
 *
 * 為什麼是 factory function 而非 class？
 * 1. 函式語意更純粹，沒有 `this` 綁定問題
 * 2. 測試時容易 mock / 建立隔離實例
 * 3. 與 PRD §3.2.8「簡易 pub/sub」規模一致
 */
export const createEventBus = <TMap extends Record<string, unknown>>(): IEventBus<TMap> => {
  // 用 Map<eventName, Set<listener>> 實作。
  // listener 用 Set 確保同一個 listener 不會重複註冊；訂閱數小（< 數十），效能無虞。
  const listeners = new Map<keyof TMap, Set<TListener<unknown>>>();

  const on = <K extends keyof TMap>(event: K, listener: TListener<TMap[K]>): (() => void) => {
    let set = listeners.get(event);
    if (!set) {
      set = new Set();
      listeners.set(event, set);
    }
    set.add(listener as TListener<unknown>);
    // 回傳的 unsubscribe 函式 closure 抓住 set 引用，呼叫時直接 delete
    return () => {
      set?.delete(listener as TListener<unknown>);
    };
  };

  const off = <K extends keyof TMap>(event: K, listener: TListener<TMap[K]>): void => {
    listeners.get(event)?.delete(listener as TListener<unknown>);
  };

  const emit = <K extends keyof TMap>(event: K, payload: TMap[K]): void => {
    const set = listeners.get(event);
    if (!set) return;
    // 拷貝一份再迭代：listener 內部如果順便取消訂閱不會影響本次 emit
    for (const listener of [...set]) {
      (listener as TListener<TMap[K]>)(payload);
    }
  };

  const clear = (): void => {
    listeners.clear();
  };

  return { emit, on, off, clear };
};
